import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { useFindAllActivities } from "~/gen/api"
import { validateLatitudeLongitudeTogether } from "~/pages/Admin/components/Fields/coordinatesSchema"
import { productionSiteFormSchema } from "~/pages/SupplierPortal/ProductionSites/schemas/productionSiteForm.schema"
import { useGetOperations } from "~/utils/operations"

export function useGetProductionSiteFormSchema(): {
  readonly schema: typeof productionSiteFormSchema
  readonly isLoading: boolean
  readonly isError: boolean
} {
  const { t } = useTranslation("supplier")
  const { data: activities, isPending, isError } = useFindAllActivities()
  const activitiesTrnaslations = useGetOperations({
    translationScope: "uniqueActivitiesLabels",
  })

  const activityOptions: ReadonlyArray<{
    readonly label: string
    readonly value: string
  }> = useMemo(() => {
    const list = activities?.data ?? []
    return list.map((a) => ({
      label: activitiesTrnaslations[a.name]?.name ?? a.name,
      value: a.id,
    }))
  }, [activities, activitiesTrnaslations])

  const registrationIdTypeOptions: ReadonlyArray<{
    readonly label: string
    readonly value: string
    readonly description?: string
  }> = useMemo(
    () => [
      {
        label: "VAT",
        value: "VAT",
        description: t("productionSiteModal.registrationIdDescriptions.VAT"),
      },
      {
        label: "EORI",
        value: "EORI",
        description: t("productionSiteModal.registrationIdDescriptions.EORI"),
      },
      {
        label: "GLN",
        value: "GLN",
        description: t("productionSiteModal.registrationIdDescriptions.GLN"),
      },
      {
        label: "DUNS",
        value: "DUNS",
        description: t("productionSiteModal.registrationIdDescriptions.DUNS"),
      },
      {
        label: "RCS",
        value: "RCS",
        description: t("productionSiteModal.registrationIdDescriptions.RCS"),
      },
      {
        label: "OTHER",
        value: "OTHER",
        description: t("productionSiteModal.registrationIdDescriptions.OTHER"),
      },
    ],
    [t]
  )

  const schema = useMemo(() => {
    const shape = productionSiteFormSchema.shape
    return z
      .object({
        name: shape.name.meta({
          ...shape.name.meta,
          label: t("productionSiteModal.labels.name"),
          tooltip: t("productionSiteModal.tooltips.name"),
          placeholder: t("productionSiteModal.placeholders.name"),
        }),
        registrationIds: shape.registrationIds.meta({
          ...shape.registrationIds.meta,
          label: t("productionSiteModal.labels.registrationId"),
          tooltip: t("productionSiteModal.tooltips.registrationId"),
          placeholder: t("productionSiteModal.placeholders.registrationId"),
          options: registrationIdTypeOptions,
        }),
        internalCode: shape.internalCode.meta({
          ...shape.internalCode.meta,
          label: t("productionSiteModal.labels.internalCode"),
          tooltip: t("productionSiteModal.tooltips.internalCode"),
          placeholder: t("productionSiteModal.placeholders.internalCode"),
        }),
        performedActivities: shape.performedActivities.meta({
          ...shape.performedActivities.meta,
          label: t("productionSiteModal.labels.performedActivities"),
          tooltip: t("productionSiteModal.tooltips.performedActivities"),
          options: activityOptions,
          placeholder: t(
            "productionSiteModal.placeholders.performedActivities"
          ),
        }),
        street: shape.street.meta({
          ...shape.street.meta,
          label: t("productionSiteModal.labels.street"),
          tooltip: t("productionSiteModal.tooltips.street"),
          placeholder: t("productionSiteModal.placeholders.street"),
        }),
        city: shape.city.meta({
          ...shape.city.meta,
          label: t("productionSiteModal.labels.city"),
          tooltip: t("productionSiteModal.tooltips.city"),
          placeholder: t("productionSiteModal.placeholders.city"),
        }),
        postalCode: shape.postalCode.meta({
          ...shape.postalCode.meta,
          label: t("productionSiteModal.labels.postalCode"),
          tooltip: t("productionSiteModal.tooltips.postalCode"),
          placeholder: t("productionSiteModal.placeholders.postalCode"),
        }),
        countryIso3166: shape.countryIso3166.meta({
          ...shape.countryIso3166.meta,
          label: t("productionSiteModal.labels.country"),
          tooltip: t("productionSiteModal.tooltips.country"),
          placeholder: t("productionSiteModal.placeholders.country"),
        }),
        longitude: shape.longitude.meta({
          ...shape.longitude.meta,
          label: t("productionSiteModal.labels.longitude"),
          tooltip: t("productionSiteModal.tooltips.longitude"),
          placeholder: t("productionSiteModal.placeholders.longitude"),
        }),
        latitude: shape.latitude.meta({
          ...shape.latitude.meta,
          label: t("productionSiteModal.labels.latitude"),
          tooltip: t("productionSiteModal.tooltips.latitude"),
          placeholder: t("productionSiteModal.placeholders.latitude"),
        }),
      })
      .superRefine(validateLatitudeLongitudeTogether)
  }, [activityOptions, registrationIdTypeOptions, t])

  return {
    schema,
    isLoading: isPending,
    isError,
  }
}
